import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Calendar, Clock, BookOpen, ChevronDown, ChevronRight, Printer } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import { formatDate, generateId } from '../../utils/helpers';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';
import DateInput from '../../components/common/DateInput';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const typeColors = {
  'formative': 'bg-blue-100 text-blue-700 border-blue-200',
  'summative': 'bg-blue-100 text-blue-700 border-blue-200',
  'slip-test': 'bg-orange-100 text-orange-700 border-orange-200',
  'revision': 'bg-green-100 text-green-700 border-green-200',
};

const typeLabels = {
  'formative': 'Formative Assessment',
  'summative': 'Summative Assessment',
  'slip-test': 'Slip Test',
  'revision': 'Revision Test',
};

const subjectColors = [
  'bg-indigo-50 border-indigo-200 text-indigo-700',
  'bg-green-50 border-green-200 text-green-700',
  'bg-orange-50 border-orange-200 text-orange-700',
  'bg-pink-50 border-pink-200 text-pink-700',
  'bg-teal-50 border-teal-200 text-teal-700',
  'bg-blue-50 border-blue-200 text-blue-700',
  'bg-yellow-50 border-yellow-200 text-yellow-700',
  'bg-red-50 border-red-200 text-red-700',
];

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function ExamSchedule() {
  const {
    exams, examTimetable, classes, subjects, students, parents, staff, academicYear,
    saveExam: apiSaveExam, updateExam: apiUpdateExam, deleteExam: apiDeleteExam,
    saveExamTimetable: apiSaveEntry, updateExamTimetable: apiUpdateEntry, deleteExamTimetable: apiDeleteEntry,
  } = useApp();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'teacher';
  const isAdminOnly = currentUser?.role === 'admin';
  const isTeacherRole = currentUser?.role === 'teacher';
  const teacherProfile = isTeacherRole ? staff.find(s => s.id === currentUser?.profileId) : null;
  const teacherClassIds = teacherProfile?.classIds || [];
  const teacherSubjectIds = teacherProfile?.subjectIds || [];

  // Parent: resolve children for selector
  const isParent = currentUser?.role === 'parent';
  const parentRecord = isParent ? parents.find(p => p.id === currentUser?.profileId) : null;
  const myChildren = parentRecord ? students.filter(s => parentRecord.studentIds?.includes(s.id)) : [];
  const [selectedChildId, setSelectedChildId] = useState(myChildren[0]?.id || null);
  const selectedChild = myChildren.find(c => c.id === selectedChildId) || myChildren[0];

  // Determine which classId(s) to filter by
  const myClassIds = (() => {
    if (currentUser?.role === 'student') {
      const s = students.find(s => s.id === currentUser?.profileId);
      return s ? [s.classId] : [];
    }
    if (isParent) {
      return selectedChild ? [selectedChild.classId] : myChildren.map(c => c.classId);
    }
    if (isTeacherRole) return teacherClassIds.length ? teacherClassIds : null;
    return null; // admin — no filter
  })();

  // Filter exam timetable entries to only those relevant to this user's class(es) and subject(s)
  const filterEntries = (entries) => {
    let result = entries;
    if (myClassIds) result = result.filter(e => (e.classIds || []).some(cid => myClassIds.includes(cid)));
    if (isTeacherRole && teacherSubjectIds.length) result = result.filter(e => teacherSubjectIds.includes(e.subjectId));
    return result;
  };

  const [expandedExams, setExpandedExams] = useState({});
  const [filterClass, setFilterClass] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter exams list to only those with at least one timetable entry for this student's class
  // Most recently scheduled exam first (by its own start date, not creation
  // order — a slip test added today for last month still belongs with the
  // older exams, not at the top). Exams with no start date fall to the end.
  const visibleExams = (myClassIds
    ? exams.filter(exam => {
        const entries = examTimetable.filter(e => e.examId === exam.id);
        return filterEntries(entries).length > 0;
      })
    : exams)
    .filter(exam => statusFilter === 'all' || exam.status === statusFilter)
    .slice().sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));

  // Exam modal
  const [examModal, setExamModal] = useState(false);
  const [examForm, setExamForm] = useState({ name: '', type: 'formative', startDate: '', endDate: '', status: 'scheduled', academicYear: academicYear || '2025-2026', classIds: [] });
  const [editingExam, setEditingExam] = useState(null);

  // Timetable entry modal
  const [entryModal, setEntryModal] = useState(false);
  const [entryExamId, setEntryExamId] = useState('');
  const [entryRows, setEntryRows] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);

  // classMaxMarks: { classId: 'nn' } — a per-class override on top of the
  // row's own maxMarks; a class not present here (or an empty string) just
  // falls back to that shared default, e.g. for a shared exam session where
  // Nursery is out of 20 and Class 8 is out of 100.
  const blankRow = (allClassIds) => ({ _key: Date.now() + Math.random(), date: '', subjectId: '', startTime: '09:00', endTime: '12:00', maxMarks: '100', classIds: allClassIds, classMaxMarks: {}, syllabus: '', classSyllabus: {} });
  // When a teacher opens "Edit Syllabus" on one of their own subject's
  // entries, the modal shows just the syllabus textarea — the date/time/
  // class fields stay in entryRows unchanged (so saveEntries still submits
  // valid values for them) but aren't rendered or editable in this mode.
  const [syllabusOnlyMode, setSyllabusOnlyMode] = useState(false);

  const openAddExam = () => {
    setExamForm({ name: '', type: 'formative', startDate: '', endDate: '', status: 'scheduled', academicYear: academicYear || '2025-2026', classIds: classes.map(c => c.id) });
    setEditingExam(null);
    setExamModal(true);
  };
  const openEditExam = (e) => { setExamForm(e); setEditingExam(e); setExamModal(true); };
  const removeExam = (id) => { if (window.confirm('Delete exam?')) apiDeleteExam(id); };
  const saveExam = async () => {
    if (editingExam) await apiUpdateExam(editingExam.id, { ...examForm, id: editingExam.id });
    else await apiSaveExam({ ...examForm, id: generateId('EXM') });
    setExamModal(false);
  };

  const openAddEntry = (examId) => {
    const allClassIds = classes.map(c => c.id);
    setEntryExamId(examId);
    setEntryRows([blankRow(allClassIds)]);
    setEditingEntry(null);
    setSyllabusOnlyMode(false);
    setEntryModal(true);
  };
  const openEditEntry = (entry, syllabusOnly = false) => {
    setEntryExamId(entry.examId);
    const classMaxMarks = Object.fromEntries(
      Object.entries(entry.classMaxMarks || {}).map(([cid, v]) => [cid, String(v)])
    );
    setEntryRows([{ _key: entry.id, date: entry.date, subjectId: entry.subjectId, startTime: entry.startTime, endTime: entry.endTime, maxMarks: String(entry.maxMarks ?? 100), classIds: entry.classIds || [], classMaxMarks, syllabus: entry.syllabus || '', classSyllabus: { ...(entry.classSyllabus || {}) } }]);
    setEditingEntry(entry);
    setSyllabusOnlyMode(syllabusOnly);
    setEntryModal(true);
  };
  const removeEntry = (id) => { if (window.confirm('Remove this schedule entry?')) apiDeleteEntry(id); };

  // Strips blank overrides (an empty box means "use the row's shared Max
  // Marks", not "override to zero") and converts the rest to numbers.
  const packClassMaxMarks = (row) => Object.fromEntries(
    Object.entries(row.classMaxMarks || {})
      .filter(([cid, v]) => (row.classIds || []).includes(cid) && String(v).trim() !== '')
      .map(([cid, v]) => [cid, Number(v)])
  );
  // Same idea as packClassMaxMarks — a blank override box means "use the
  // shared Syllabus / Portions above", not "wipe it to empty".
  const packClassSyllabus = (row) => Object.fromEntries(
    Object.entries(row.classSyllabus || {})
      .filter(([cid, v]) => (row.classIds || []).includes(cid) && String(v).trim() !== '')
      .map(([cid, v]) => [cid, String(v).trim()])
  );

  const saveEntries = async () => {
    const valid = entryRows.filter(r => r.date && r.subjectId);
    // A row missing date/subject is silently dropped below rather than
    // saved — make sure that's never invisible: without this, an admin who
    // added a second/third subject but left one field blank would see the
    // modal just close with no error and no clue a row went unsaved.
    if (!editingEntry && valid.length < entryRows.length) {
      const missing = entryRows.length - valid.length;
      if (!window.confirm(`${missing} subject row${missing > 1 ? 's are' : ' is'} missing a date or subject and won't be saved. Continue anyway?`)) return;
    }
    if (editingEntry) {
      const r = valid[0];
      if (r) {
        // A teacher's syllabus-only edit never sends date/subject/time/
        // classIds/classMaxMarks — those stay admin-only, and omitting them
        // here means the PUT simply leaves those columns untouched.
        const body = syllabusOnlyMode
          ? { examId: editingEntry.examId, syllabus: r.syllabus || '', classSyllabus: packClassSyllabus(r) }
          : { examId: editingEntry.examId, date: r.date, subjectId: r.subjectId, startTime: r.startTime, endTime: r.endTime,
              maxMarks: Number(r.maxMarks) || 100, classIds: r.classIds, classMaxMarks: packClassMaxMarks(r),
              syllabus: r.syllabus || '', classSyllabus: packClassSyllabus(r) };
        await apiUpdateEntry(editingEntry.id, body);
      }
    } else {
      const newEntries = valid.map(r => ({ id: generateId('ET'), examId: entryExamId, date: r.date, subjectId: r.subjectId, startTime: r.startTime, endTime: r.endTime, maxMarks: Number(r.maxMarks) || 100, classIds: r.classIds, classMaxMarks: packClassMaxMarks(r), syllabus: r.syllabus || '', classSyllabus: packClassSyllabus(r) }));
      await Promise.all(newEntries.map(e => apiSaveEntry(e)));
    }
    setEntryModal(false);
  };

  const updateRow = (key, field, val) => setEntryRows(rows => rows.map(r => r._key === key ? { ...r, [field]: val } : r));
  const toggleRowClass = (key, classId) => setEntryRows(rows => rows.map(r => {
    if (r._key !== key) return r;
    const selected = r.classIds.includes(classId);
    // Untoggling a class drops its overrides too — a leftover value for a
    // class no longer on this row would just be dead state.
    const classMaxMarks = { ...(r.classMaxMarks || {}) };
    const classSyllabus = { ...(r.classSyllabus || {}) };
    if (selected) { delete classMaxMarks[classId]; delete classSyllabus[classId]; }
    return { ...r, classIds: selected ? r.classIds.filter(id => id !== classId) : [...r.classIds, classId], classMaxMarks, classSyllabus };
  }));
  const updateClassMaxMarks = (key, classId, val) => setEntryRows(rows => rows.map(r =>
    r._key === key ? { ...r, classMaxMarks: { ...(r.classMaxMarks || {}), [classId]: val } } : r));
  const updateClassSyllabus = (key, classId, val) => setEntryRows(rows => rows.map(r =>
    r._key === key ? { ...r, classSyllabus: { ...(r.classSyllabus || {}), [classId]: val } } : r));
  // Inherit date/start/end time from the row above — "Add another subject"
  // exists specifically to schedule another subject at the SAME slot, so a
  // blank date here just means the admin has to notice and re-fill it. When
  // they don't, saveEntries silently drops the whole row (it requires date +
  // subjectId) with no warning, and the second subject never gets saved at
  // all — looking exactly like "can't add multiple subjects at the same time".
  const addRow = () => setEntryRows(rows => {
    const last = rows[rows.length - 1];
    const blank = blankRow(classes.map(c => c.id));
    return [...rows, last ? { ...blank, date: last.date, startTime: last.startTime, endTime: last.endTime } : blank];
  });
  const removeRow = (key) => setEntryRows(rows => rows.filter(r => r._key !== key));
  const setRowClasses = (key, ids) => setEntryRows(rows => rows.map(r => {
    if (r._key !== key) return r;
    // Drop overrides for any class no longer selected
    const classMaxMarks = Object.fromEntries(
      Object.entries(r.classMaxMarks || {}).filter(([cid]) => ids.includes(cid)));
    const classSyllabus = Object.fromEntries(
      Object.entries(r.classSyllabus || {}).filter(([cid]) => ids.includes(cid)));
    return { ...r, classIds: ids, classMaxMarks, classSyllabus };
  }));

  const today = new Date().toISOString().split('T')[0];

  // Solid accent for the exam card's left rail + icon chip — same hue
  // family as typeColors above, just resolved to a fill instead of a
  // light badge background.
  const typeAccent  = { formative: 'bg-blue-600', summative: 'bg-blue-600', 'slip-test': 'bg-orange-500', revision: 'bg-green-600' };
  const typeIconBg  = { formative: 'bg-blue-50 text-blue-600', summative: 'bg-blue-50 text-blue-600',
    'slip-test': 'bg-orange-50 text-orange-600', revision: 'bg-green-50 text-green-600' };

  const StatusPill = ({ status }) => {
    if (status === 'ongoing') return (
      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />Ongoing
      </span>
    );
    if (status === 'completed') return (
      <span className="text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 px-2.5 py-0.5 rounded-full">Completed</span>
    );
    return <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-full">Upcoming</span>;
  };

  // How far into an ongoing exam "today" falls — null for anything that
  // isn't currently running, or that's missing the dates to measure from.
  const examProgress = (exam) => {
    if (exam.status !== 'ongoing' || !exam.startDate || !exam.endDate) return null;
    const start = new Date(exam.startDate + 'T00:00:00'), end = new Date(exam.endDate + 'T00:00:00'), now = new Date(today + 'T00:00:00');
    const totalDays = Math.max(1, Math.round((end - start) / 86400000) + 1);
    const dayNum = Math.min(totalDays, Math.max(1, Math.round((now - start) / 86400000) + 1));
    return { dayNum, totalDays, pct: Math.round((dayNum / totalDays) * 100) };
  };

  const printTimetable = async (exam, entries) => {
    const school = await getSchoolInfo();
    // Group entries by classId
    const classBlocks = classes.map(cls => {
      const clsEntries = entries.filter(e => (e.classIds || []).includes(cls.id))
        .sort((a, b) => a.date.localeCompare(b.date));
      if (clsEntries.length === 0) return null;
      const rows = clsEntries.map((e, i) => {
        const sub = subjects.find(s => s.id === e.subjectId);
        const dateObj = new Date(e.date + 'T00:00:00');
        const day = DAYS[dateObj.getDay()];
        return `<tr>
          <td>${i + 1}</td>
          <td><strong>${formatDate(e.date)}</strong></td>
          <td>${day}</td>
          <td>${formatTime12(e.startTime)} – ${formatTime12(e.endTime)}</td>
          <td><strong>${sub?.name || '—'}</strong> <span style="color:#888">(${sub?.code || '—'})</span></td>
        </tr>`;
      }).join('');
      return `
        <div class="class-block">
          <div class="class-title">${cls.name} – Section ${cls.section}</div>
          <table>
            <thead><tr><th>#</th><th>Date</th><th>Day</th><th>Time</th><th>Subject</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    }).filter(Boolean).join('');

    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.open(); win.document.write(`<!DOCTYPE html>
<html><head><title>Exam Timetable — ${exam.name}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Roboto',sans-serif; padding:24px 32px; color:#111; font-size:12px; }
  .school-header { margin-bottom:16px; }
  .meta { display:flex; justify-content:space-between; margin-bottom:20px; font-size:12px; color:#555; }
  .meta .title { font-size:15px; font-weight:700; color:#1e3a8a; }
  .class-block { margin-bottom:20px; page-break-inside:avoid; }
  .class-title { font-size:13px; font-weight:700; color:#fff; background:#1e3a8a; padding:6px 12px; border-radius:6px 6px 0 0; }
  table { width:100%; border-collapse:collapse; }
  thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:11px; padding:7px 10px; text-align:left; border-bottom:2px solid #d1d5db; }
  td { padding:6px 10px; border-bottom:1px solid #f3f4f6; font-size:12px; }
  tr:last-child td { border-bottom:none; }
  .footer { text-align:center; font-size:11px; color:#aaa; margin-top:24px; padding-top:8px; border-top:1px dashed #e5e7eb; }
  .print-btn { text-align:center; margin-top:16px; }
  @media print { .print-btn{display:none;} body{padding:12px;} * { color:#000 !important; } }
</style></head><body>
<div class="school-header">
  ${schoolHeader(school, '#1e3a8a', `Examination Timetable &mdash; ${exam.name} &mdash; Academic Year ${exam.academicYear || '2025-2026'}`)}
</div>
<div class="meta">
  <div><div class="title">${exam.name}</div><div style="margin-top:2px">${formatDate(exam.startDate)} – ${formatDate(exam.endDate)}</div></div>
  <div style="text-align:right">Date: <strong>${(() => { const d = new Date(); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}</strong></div>
</div>
${classBlocks}
<div class="footer">Computer-generated &mdash; ${school.name}</div>
<div class="print-btn"><button onclick="window.print()" style="background:#1e3a8a;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:13px;cursor:pointer;font-family:'Roboto',sans-serif">🖨 Print</button></div>
</body></html>`); win.document.close();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Exam Schedule</h1>
          <p className="text-gray-500 text-sm">{visibleExams.length} exam{visibleExams.length !== 1 ? 's' : ''} this academic year</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Child selector for parents with multiple children */}
          {isParent && myChildren.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {myChildren.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelectedChildId(c.id)}
                  className={`px-4 py-1 rounded-lg text-sm font-medium transition-colors border ${
                    c.id === selectedChild?.id
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {c.firstName} {c.lastName}
                </button>
              ))}
            </div>
          )}
          <div className="inline-flex bg-gray-50 border border-gray-200 rounded-lg p-0.5 gap-0.5">
            {['all', 'scheduled', 'ongoing', 'completed'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 text-xs font-semibold rounded-md capitalize transition-colors ${
                  statusFilter === s ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {s === 'all' ? 'All' : s === 'scheduled' ? 'Upcoming' : s}
              </button>
            ))}
          </div>
          {isAdmin && (
            <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
              <option value="">All Classes</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
            </select>
          )}
          {isAdminOnly && (
            <button onClick={openAddExam} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1 rounded-lg text-sm font-medium">
              <Plus size={16} /> Schedule Exam
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {visibleExams.map((exam, examIdx) => {
          // Only the most recent exam (top of the list) starts expanded —
          // older exams start closed so the page opens on what matters
          // right now instead of a long wall of expanded subject lists.
          // Once a user toggles any exam themselves, that explicit choice
          // (open or closed) always wins over this default.
          const isOpen = expandedExams[exam.id] !== undefined ? expandedExams[exam.id] : examIdx === 0;
          const entries = filterEntries(
            examTimetable.filter(e => e.examId === exam.id)
          ).filter(e => !filterClass || (e.classIds || []).includes(filterClass))
           .sort((a, b) => a.date.localeCompare(b.date));

          const progress = examProgress(exam);

          // Group entries into date nodes for the timeline — entries are
          // already sorted by date, so groups come out in date order too.
          const dateGroups = [];
          entries.forEach(e => {
            let g = dateGroups.find(g => g.date === e.date);
            if (!g) { g = { date: e.date, rows: [] }; dateGroups.push(g); }
            g.rows.push(e);
          });

          return (
            <div key={exam.id} className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden ${exam.status === 'completed' ? 'opacity-80' : ''}`}>
              {/* Exam header */}
              <div className="flex items-stretch gap-3 px-4 py-4">
                <div className={`w-1 rounded-full flex-shrink-0 ${typeAccent[exam.type] || 'bg-gray-300'}`} />
                <button
                  onClick={() => setExpandedExams(prev => ({ ...prev, [exam.id]: !isOpen }))}
                  className="flex items-start gap-3 flex-1 text-left min-w-0"
                >
                  <div className={`p-2 rounded-lg flex-shrink-0 ${typeIconBg[exam.type] || 'bg-gray-100 text-gray-500'}`}>
                    <Calendar size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold truncate">{exam.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${typeColors[exam.type] || 'bg-gray-100 border-gray-200'}`}>
                        {typeLabels[exam.type] || exam.type}
                      </span>
                      <StatusPill status={exam.status} />
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {formatDate(exam.startDate)} – {formatDate(exam.endDate)}
                      <span className="mx-2">·</span>
                      {exam.academicYear}
                      <span className="mx-2">·</span>
                      {entries.length} subject{entries.length !== 1 ? 's' : ''} scheduled
                    </p>
                    {progress && (
                      <div className="mt-2 max-w-[220px]">
                        <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full rounded-full bg-amber-500" style={{ width: `${progress.pct}%` }} />
                        </div>
                        <div className="text-[10px] font-semibold text-gray-400 mt-1">Day {progress.dayNum} of {progress.totalDays}</div>
                      </div>
                    )}
                  </div>
                  <span className="ml-2 flex-shrink-0 mt-1 text-gray-400">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                </button>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {entries.length > 0 && (
                    <button onClick={() => printTimetable(exam, entries)} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50">
                      <Printer size={13}/> Print
                    </button>
                  )}
                  {isAdminOnly && (
                    <>
                      <button onClick={() => openAddEntry(exam.id)} className="flex items-center gap-1 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-3 py-1.5 rounded-lg font-medium">
                        <Plus size={13} /> Add Subject
                      </button>
                      <button onClick={() => openEditExam(exam)} className="p-1.5 hover:bg-indigo-50 rounded-lg text-indigo-500"><Edit2 size={14} /></button>
                      <button onClick={() => removeExam(exam.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </div>

              {/* Timeline */}
              {isOpen && (
                <div className="px-4 pb-4">
                  {entries.length === 0 ? (
                    <p className="text-center py-6 text-gray-400 text-sm">No subjects scheduled yet</p>
                  ) : (
                    <div>
                      {dateGroups.map((grp, gi) => {
                        const isToday = grp.date === today;
                        const dayName = DAYS[new Date(grp.date + 'T00:00:00').getDay()];
                        return (
                          <div key={grp.date} className="relative pl-6" style={{ marginTop: gi === 0 ? 4 : 16 }}>
                            {gi < dateGroups.length - 1 && (
                              <div className="absolute left-[6px] top-5 bottom-[-16px] w-px bg-gray-200" />
                            )}
                            <div className={`absolute left-0 top-0.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                              isToday ? 'bg-indigo-600 border-indigo-600' : 'bg-indigo-50 border-indigo-400'}`}>
                              <span className={`w-1 h-1 rounded-full ${isToday ? 'bg-white' : 'bg-indigo-500'}`} />
                            </div>
                            <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                              {formatDate(grp.date)}
                              <span className="font-medium text-gray-400">· {dayName}</span>
                              {isToday && <span className="text-[10px] font-bold text-white bg-indigo-600 px-1.5 py-0.5 rounded-full uppercase">Today</span>}
                            </div>

                            {grp.rows.map((entry, idx) => {
                              const subject = subjects.find(s => s.id === entry.subjectId);
                              const colorClass = subjectColors[idx % subjectColors.length];

                              // Which class(es) this viewer should see on this
                              // slot: their own single class, the admin's
                              // class filter if one is set, or every class
                              // this entry covers otherwise (admin/teacher
                              // looking at "All Classes").
                              const classesToShow = (myClassIds && myClassIds.length === 1)
                                ? (entry.classIds || []).filter(cid => cid === myClassIds[0])
                                : filterClass
                                  ? (entry.classIds || []).filter(cid => cid === filterClass)
                                  : (entry.classIds || []);
                              const resolvedFor = (cid) => ({
                                marks: entry.classMaxMarks?.[cid] ?? entry.maxMarks ?? 100,
                                syllabus: (entry.classSyllabus?.[cid] ?? entry.syllabus ?? '').trim(),
                              });
                              // Classes that end up with the same syllabus text
                              // (the common case — everyone on the default)
                              // share one line instead of repeating it per class.
                              const syllabusGroups = [];
                              classesToShow.forEach(cid => {
                                const { syllabus } = resolvedFor(cid);
                                if (!syllabus) return;
                                const cls = classes.find(c => c.id === cid);
                                const label = cls ? `${cls.name}-${cls.section}` : cid;
                                const g = syllabusGroups.find(g => g.text === syllabus);
                                if (g) g.labels.push(label); else syllabusGroups.push({ text: syllabus, labels: [label] });
                              });

                              return (
                                <div key={entry.id} className={`mt-2 rounded-lg border p-2.5 ${isToday ? 'bg-indigo-50/50 border-indigo-100' : 'bg-gray-50 border-gray-100'}`}>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="flex items-center gap-1 text-xs font-semibold text-gray-600 whitespace-nowrap">
                                      <Clock size={11} className="opacity-50"/>
                                      {formatTime12(entry.startTime)} – {formatTime12(entry.endTime)}
                                    </span>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-bold ${colorClass}`}>
                                      <BookOpen size={11}/>{subject?.name || '—'}
                                    </span>
                                    <div className="ml-auto flex flex-wrap gap-1">
                                      {classesToShow.map(cid => {
                                        const cls = classes.find(c => c.id === cid);
                                        const { marks } = resolvedFor(cid);
                                        return (
                                          <span key={cid} className="inline-flex items-center gap-1 text-xs font-semibold bg-white border border-gray-200 rounded-md px-2 py-0.5">
                                            {cls ? `${cls.name}-${cls.section}` : cid}
                                            <span className="text-indigo-600 font-bold">{marks}</span>
                                          </span>
                                        );
                                      })}
                                    </div>
                                    {(isAdminOnly || (isTeacherRole && teacherSubjectIds.includes(entry.subjectId))) && (
                                      <div className="flex items-center gap-1">
                                        {isAdminOnly && (
                                          <>
                                            <button onClick={() => openEditEntry(entry)} className="p-1 hover:bg-indigo-100 rounded text-indigo-400" title="Edit Entry"><Edit2 size={13}/></button>
                                            <button onClick={() => removeEntry(entry.id)} className="p-1 hover:bg-red-100 rounded text-red-400"><Trash2 size={13}/></button>
                                          </>
                                        )}
                                        {!isAdminOnly && isTeacherRole && teacherSubjectIds.includes(entry.subjectId) && (
                                          <button onClick={() => openEditEntry(entry, true)} className="p-1 hover:bg-indigo-100 rounded text-indigo-400" title="Edit Syllabus"><Edit2 size={13}/></button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {syllabusGroups.map((g, si) => (
                                    // whitespace-pre-line — a syllabus typed
                                    // as several lines in the textarea was
                                    // collapsing back onto one line here;
                                    // plain text in a <div> ignores \n by
                                    // default, textareas don't.
                                    <div key={si} className="mt-1.5 text-xs text-gray-500 leading-relaxed whitespace-pre-line">
                                      <span className="font-semibold text-gray-700">
                                        {classesToShow.length > 1 ? `${g.labels.join(', ')}: ` : 'Syllabus: '}
                                      </span>
                                      {g.text}
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Exam Modal — admin only */}
      <Modal isOpen={examModal} onClose={() => setExamModal(false)} title={editingExam ? 'Edit Exam' : 'Schedule Exam'} size="lg">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Exam Name</label>
            <input value={examForm.name || ''} onChange={e => setExamForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select value={examForm.type || 'unit-test'} onChange={e => setExamForm(f => ({ ...f, type: e.target.value }))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                {Object.entries(typeLabels).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select value={examForm.status || 'scheduled'} onChange={e => setExamForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                {['scheduled', 'ongoing', 'completed'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <DateInput value={examForm.startDate || ''} onChange={e => setExamForm(f => ({ ...f, startDate: e.target.value }))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <DateInput value={examForm.endDate || ''} onChange={e => setExamForm(f => ({ ...f, endDate: e.target.value }))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setExamModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={saveExam} className="px-4 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
        </div>
      </Modal>

      {/* Timetable Entry Modal */}
      <Modal isOpen={entryModal} onClose={() => setEntryModal(false)}
        title={syllabusOnlyMode ? 'Edit Syllabus' : editingEntry ? 'Edit Schedule Entry' : 'Add Subjects to Schedule'} size="xl">
        <div className="space-y-4 max-h-[68vh] overflow-y-auto px-0.5 -mx-0.5">
          {entryRows.map((row, idx) => {
            const allSelected = classes.length > 0 && (row.classIds || []).length === classes.length;
            return (
            <div key={row._key} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
              {entryRows.length > 1 && (
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Subject {idx + 1}</span>
                  <button onClick={() => removeRow(row._key)} className="text-gray-400 hover:text-red-600 p-1 -m-1" title="Remove">
                    <Trash2 size={15} />
                  </button>
                </div>
              )}

              {/* A teacher editing just the syllabus on their own subject's
                  entry doesn't get the date/time/class fields at all — only
                  admin can move an exam's schedule around, so those stay
                  read-only context here instead of editable inputs. */}
              {syllabusOnlyMode ? (
                <div className="text-sm">
                  <span className="font-semibold text-gray-800">{subjects.find(s => s.id === row.subjectId)?.name || 'Subject'}</span>
                  <span className="text-gray-400 mx-1.5">·</span>
                  <span className="text-gray-500">{formatDate(row.date)}</span>
                </div>
              ) : (
                <>
                  {/* No standalone Max Marks field — each class in the picker below
                      carries its own max marks input, so a shared field here was
                      just a 5th number that didn't match any of them once classes
                      had their own values. row.maxMarks lives on in state only as
                      the fallback placeholder those per-class boxes show blank. */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                      <DateInput value={row.date} onChange={e => updateRow(row._key, 'date', e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
                      <select value={row.subjectId} onChange={e => updateRow(row._key, 'subjectId', e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300">
                        <option value="">Select…</option>
                        {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Start</label>
                      <input type="time" value={row.startTime} onChange={e => updateRow(row._key, 'startTime', e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">End</label>
                      <input type="time" value={row.endTime} onChange={e => updateRow(row._key, 'endTime', e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-medium text-gray-500">
                        Classes <span className="text-gray-400">({(row.classIds || []).length} selected)</span>
                      </label>
                      <button type="button"
                        onClick={() => setRowClasses(row._key, allSelected ? [] : classes.map(c => c.id))}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
                        {allSelected ? 'Clear all' : 'Select all'}
                      </button>
                    </div>
                    {/* Selected classes carry an inline Max Marks box (placeholder =
                        the shared Max Marks above; fill it only where a class is
                        marked out of something different). Unselected classes are
                        just a chip to click. */}
                    <div className="flex flex-wrap gap-1.5">
                      {classes.map(c => {
                        const on = (row.classIds || []).includes(c.id);
                        if (!on) return (
                          <button key={c.id} type="button" onClick={() => toggleRowClass(row._key, c.id)}
                            className="px-2 py-1 rounded-md text-xs font-medium border bg-white text-gray-600 border-gray-300 hover:border-indigo-400 transition-colors">
                            {c.name}-{c.section}
                          </button>
                        );
                        return (
                          <span key={c.id} className="inline-flex items-stretch rounded-md overflow-hidden border border-indigo-600">
                            <button type="button" onClick={() => toggleRowClass(row._key, c.id)}
                              className="px-2 py-1 text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700"
                              title="Click to remove">
                              {c.name}-{c.section}
                            </button>
                            <input type="number" min="1" placeholder={String(row.maxMarks || 100)}
                              value={row.classMaxMarks?.[c.id] ?? ''}
                              onChange={e => updateClassMaxMarks(row._key, c.id, e.target.value)}
                              title="Max marks for this class (blank = same as above)"
                              className="w-12 px-1 py-0.5 text-xs text-right bg-indigo-50 text-indigo-900 placeholder:text-indigo-300 border-l border-indigo-300 focus:outline-none focus:bg-white" />
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Syllabus / Portions {(row.classIds || []).length > 1 && <span className="text-gray-400">— default for all selected classes</span>}
                </label>
                <textarea value={row.syllabus || ''} onChange={e => updateRow(row._key, 'syllabus', e.target.value)}
                  rows={syllabusOnlyMode ? 4 : 3}
                  placeholder="e.g. Chapters 4–6: Fractions, Decimals, Simple Interest"
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
              </div>

              {/* Per-class override — same idea as the Max Marks box on each
                  class chip above: blank means "use the default above", so a
                  shared exam session can still carry different portions per
                  class (or per section) without retyping the common text. */}
              {(row.classIds || []).length > 1 && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Per-Class Syllabus Override <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="space-y-1.5 border border-dashed border-gray-300 rounded-lg p-2.5">
                    {(row.classIds || []).map(cid => {
                      const cls = classes.find(c => c.id === cid);
                      const marks = row.classMaxMarks?.[cid] || row.maxMarks || 100;
                      return (
                        <div key={cid} className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-20 text-center text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-md px-1.5 py-1 leading-tight">
                            {cls ? `${cls.name}-${cls.section}` : cid}
                            <span className="block text-[10px] font-normal opacity-70">Max {marks}</span>
                          </span>
                          <textarea rows={2} value={row.classSyllabus?.[cid] || ''}
                            onChange={e => updateClassSyllabus(row._key, cid, e.target.value)}
                            placeholder="Same as default above"
                            className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            );
          })}
          {!editingEntry && (
            <button onClick={addRow} className="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 text-sm font-medium py-1">
              <Plus size={15} /> Add another subject
            </button>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
          <button onClick={() => setEntryModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={saveEntries} className="px-4 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            {editingEntry ? 'Save' : `Save ${entryRows.filter(r => r.date && r.subjectId).length || ''} Subject${entryRows.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </Modal>
    </div>
  );
}
export default ExamSchedule;
